import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, UserX, CheckCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

interface BookingActionsProps {
  bookingId: number;
  status: 'pending' | 'confirmed' | 'cancelled' | 'no_show' | 'completed';
  startTime: string;
  onConfirm: (id: number) => Promise<void>;
  onCancel: (id: number, reason: string) => Promise<void>;
  onNoShow: (id: number) => Promise<void>;
  onComplete: (id: number) => Promise<void>;
  compact?: boolean;
}

export function BookingActions({
  bookingId,
  status,
  startTime,
  onConfirm,
  onCancel,
  onNoShow,
  onComplete,
  compact = false,
}: BookingActionsProps) {
  const { t } = useTranslation(['bookings', 'common']);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [loading, setLoading] = useState(false);

  const appointmentTime = new Date(startTime);
  const now = new Date();
  const isInPast = appointmentTime < now;
  const isToday = appointmentTime.toDateString() === now.toDateString();

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm(bookingId);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubmit = async () => {
    if (!cancelReason.trim()) return;
    setLoading(true);
    try {
      await onCancel(bookingId, cancelReason);
      setCancelDialogOpen(false);
      setCancelReason('');
    } finally {
      setLoading(false);
    }
  };

  const handleNoShow = async () => {
    setLoading(true);
    try {
      await onNoShow(bookingId);
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      await onComplete(bookingId);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {status === 'pending' && (
          <>
            <Button
              size={compact ? 'icon' : 'sm'}
              onClick={handleConfirm}
              disabled={loading}
              className={compact ? 'h-8 w-8 bg-emerald-600 hover:bg-emerald-700' : 'bg-emerald-600 hover:bg-emerald-700'}
              aria-label={t('bookings:actions.confirm')}
              title={compact ? t('bookings:actions.confirm') : undefined}
            >
              <CheckCircle className={compact ? 'h-4 w-4' : 'h-4 w-4 mr-1'} />
              {!compact && t('bookings:actions.confirm')}
            </Button>
            <Button
              size={compact ? 'icon' : 'sm'}
              variant="outline"
              onClick={() => setCancelDialogOpen(true)}
              disabled={loading}
              className={compact ? 'h-8 w-8 border-red-300 text-red-600 hover:bg-red-50' : 'border-red-300 text-red-600 hover:bg-red-50'}
              aria-label={t('bookings:actions.cancel')}
              title={compact ? t('bookings:actions.cancel') : undefined}
            >
              <XCircle className={compact ? 'h-4 w-4' : 'h-4 w-4 mr-1'} />
              {!compact && t('bookings:actions.cancel')}
            </Button>
          </>
        )}

        {status === 'confirmed' && (
          <>
            {isToday && !isInPast && (
              <Button
                size={compact ? 'icon' : 'sm'}
                onClick={handleComplete}
                disabled={loading}
                className={compact ? 'h-8 w-8 bg-soft-blue hover:bg-dark-blue' : 'bg-soft-blue hover:bg-dark-blue'}
                aria-label={t('bookings:actions.complete')}
                title={compact ? t('bookings:actions.complete') : undefined}
              >
                <CheckCheck className={compact ? 'h-4 w-4' : 'h-4 w-4 mr-1'} />
                {!compact && t('bookings:actions.complete')}
              </Button>
            )}
            {isToday && (
              <Button
                size={compact ? 'icon' : 'sm'}
                variant="outline"
                onClick={handleNoShow}
                disabled={loading}
                className={compact ? 'h-8 w-8 border-amber-300 text-amber-600 hover:bg-amber-50' : 'border-amber-300 text-amber-600 hover:bg-amber-50'}
                aria-label={t('bookings:actions.noShow')}
                title={compact ? t('bookings:actions.noShow') : undefined}
              >
                <UserX className={compact ? 'h-4 w-4' : 'h-4 w-4 mr-1'} />
                {!compact && t('bookings:actions.noShow')}
              </Button>
            )}
            {!isInPast && (
              <Button
                size={compact ? 'icon' : 'sm'}
                variant="outline"
                onClick={() => setCancelDialogOpen(true)}
                disabled={loading}
                className={compact ? 'h-8 w-8 border-red-300 text-red-600 hover:bg-red-50' : 'border-red-300 text-red-600 hover:bg-red-50'}
                aria-label={t('bookings:actions.cancel')}
                title={compact ? t('bookings:actions.cancel') : undefined}
              >
                <XCircle className={compact ? 'h-4 w-4' : 'h-4 w-4 mr-1'} />
                {!compact && t('bookings:actions.cancel')}
              </Button>
            )}
          </>
        )}
      </div>

      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('bookings:details.cancelDialogTitle')}</DialogTitle>
            <DialogDescription>
              {t('bookings:details.cancelDialogDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reason">{t('bookings:details.cancelReasonLabel')}</Label>
              <Textarea
                id="reason"
                placeholder={t('bookings:details.cancelReasonPlaceholder')}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCancelDialogOpen(false);
                setCancelReason('');
              }}
            >
              {t('common:actions.close')}
            </Button>
            <Button
              onClick={handleCancelSubmit}
              disabled={!cancelReason.trim() || loading}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('bookings:actions.cancelBooking')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
